import type { PayloadAction } from "@reduxjs/toolkit"
import { createAppSlice } from "@/app/createAppSlice"
import type { AppThunk } from "@/app/store"
import {
  searchCampingSites,
  getSiteById,
  searchCampingSitesForAdmin,
  getPendingCampingSitesForAdmin,
} from "@/services/api"
import type {
  CampingSiteMapResponse,
  CampingSiteSearchRequest,
  CampingSite,
  CampingSiteResponse,
} from "@/types/domain"

// 지도와 관련된 글로벌 store 를 저장하는 slice

export type Coordinate = {
  lat: number
  lng: number
}
export type MapGlobalState = {
  gps: Coordinate // 현재 위치 좌표,
  zoom: number // 줌 레벨
  isPermitted: boolean // 위치정보 허용 여부 (권한 설정 여부)
  isGPSFetchedByUser: boolean // 사용자가 명시적으로 현재 위치를 조회했는지 여부
  hasGPSData: boolean // GPS 데이터가 실제로 조회되었는지 여부 (초기값이 아닌지)
  campingSites: CampingSiteMapResponse[]
  status: "idle" | "loading" | "failed"
  selectedSite: CampingSite | null // 선택된 캠핑장 상세 정보
  selectedSiteStatus: "idle" | "loading" | "failed"
  bookmarkedSites: CampingSite[] // 북마크된 캠핑장 목록
  bookmarkedSitesStatus: "idle" | "loading" | "failed"
  pendingSites: CampingSiteResponse[] // 미승인 캠핑장 목록 (관리자용)
}

const initialState: MapGlobalState = {
  gps: {
    lat: 33.450701,
    lng: 126.570667,
  },
  zoom: 3,
  isPermitted: false,
  isGPSFetchedByUser: false,
  hasGPSData: false,
  campingSites: [],
  status: "idle",
  selectedSite: null,
  selectedSiteStatus: "idle",
  bookmarkedSites: [],
  bookmarkedSitesStatus: "idle",
  pendingSites: [],
}

export const mapsSlice = createAppSlice({
  name: "maps",
  initialState,

  reducers: create => ({
    setGPS: create.reducer((state, action: PayloadAction<Coordinate>) => {
      state.gps = action.payload
    }),
    setZoom: create.reducer((state, action: PayloadAction<number>) => {
      state.zoom = action.payload
    }),
    setPermitted: create.reducer((state, action: PayloadAction<boolean>) => {
      state.isPermitted = action.payload
    }),
    setGPSFetchedByUser: create.reducer(
      (state, action: PayloadAction<boolean>) => {
        state.isGPSFetchedByUser = action.payload
      },
    ),
    setHasGPSData: create.reducer((state, action: PayloadAction<boolean>) => {
      state.hasGPSData = action.payload
    }),
    setMapCenter: create.reducer((state, action: PayloadAction<Coordinate>) => {
      state.gps = action.payload
    }),
    fetchCampingSites: create.asyncThunk(
      async ({
        request,
        isAdmin,
      }: {
        request: CampingSiteSearchRequest
        isAdmin: boolean
      }) => {
        const response = isAdmin
          ? await searchCampingSitesForAdmin(request)
          : await searchCampingSites(request)
        return response.content
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "idle"
          state.campingSites = action.payload
        },
        rejected: state => {
          state.status = "failed"
        },
      },
    ),
    fetchSiteById: create.asyncThunk(
      async (id: string) => {
        const response = await getSiteById(id)
        return response
      },
      {
        pending: state => {
          state.selectedSiteStatus = "loading"
        },
        fulfilled: (state, action) => {
          state.selectedSiteStatus = "idle"
          state.selectedSite = action.payload
        },
        rejected: state => {
          state.selectedSiteStatus = "failed"
          state.selectedSite = null
        },
      },
    ),
    fetchSitesByIds: create.asyncThunk(
      async (ids: string[]) => {
        const promises = ids.map(id => getSiteById(id))
        const responses = await Promise.all(promises)
        return responses
      },
      {
        pending: state => {
          state.bookmarkedSitesStatus = "loading"
        },
        fulfilled: (state, action) => {
          state.bookmarkedSitesStatus = "idle"
          state.bookmarkedSites = action.payload
        },
        rejected: state => {
          state.bookmarkedSitesStatus = "failed"
        },
      },
    ),
    fetchPendingCampingSites: create.asyncThunk(
      async ({
        page = 0,
        size = 100,
        name,
        tagIds,
      }: {
        page?: number
        size?: number
        name?: string
        tagIds?: number[]
      } = {}) => {
        const response = await getPendingCampingSitesForAdmin(
          page,
          size,
          name,
          tagIds,
        )
        return response.content
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "idle"
          state.pendingSites = action.payload // 저장
        },
        rejected: state => {
          state.status = "failed"
        },
      },
    ),
  }),
  // You can define your selectors here. These selectors receive the slice
  // state as their first argument.
  selectors: {
    selectGPS: maps => maps.gps,
    selectIsPermitted: maps => maps.isPermitted,
    selectZoom: maps => maps.zoom,
    selectCampingSites: maps => maps.campingSites,
    selectCampingSitesStatus: maps => maps.status,
    selectIsGPSFetchedByUser: maps => maps.isGPSFetchedByUser,
    selectHasGPSData: maps => maps.hasGPSData,
    selectSelectedSite: maps => maps.selectedSite,
    selectSelectedSiteStatus: maps => maps.selectedSiteStatus,
    selectBookmarkedSites: maps => maps.bookmarkedSites,
    selectBookmarkedSitesStatus: maps => maps.bookmarkedSitesStatus,
    selectCampingSiteById: (maps, id: string) =>
      maps.campingSites.find(site => site.id === id),
    selectPendingSites: maps => maps.pendingSites,
  },
})

export const {
  setGPS,
  setPermitted,
  setZoom,
  setGPSFetchedByUser,
  setHasGPSData,
  setMapCenter,
  fetchCampingSites,
  fetchSiteById,
  fetchSitesByIds,
  fetchPendingCampingSites,
} = mapsSlice.actions
export const {
  selectGPS,
  selectIsPermitted,
  selectZoom,
  selectCampingSites,
  selectCampingSitesStatus,
  selectIsGPSFetchedByUser,
  selectHasGPSData,
  selectSelectedSite,
  selectSelectedSiteStatus,
  selectBookmarkedSites,
  selectBookmarkedSitesStatus,
  selectCampingSiteById,
  selectPendingSites,
} = mapsSlice.selectors

// GPS 조회 공통 로직
const fetchGPSPosition = async (
  dispatch: (action: PayloadAction<boolean | Coordinate>) => void,
): Promise<GeolocationPosition | null> => {
  try {
    // Safari와 기타 브라우저 모두 지원하기 위한 권한 확인 로직
    let permissionState: PermissionState = "prompt" // 기본값을 'prompt'로 설정

    // navigator.permissions API가 지원되는 브라우저에서만 실행
    if ("permissions" in navigator) {
      try {
        const permissionResult = await navigator.permissions.query({
          name: "geolocation",
        })
        permissionState = permissionResult.state
        console.log("Permission status from API:", permissionState)
      } catch (error) {
        console.warn("Permission query not supported fully:", error)
        // Safari에서는 name: 'geolocation'이 지원되지 않을 수 있음
      }
    } else {
      console.log("navigator.permissions API not supported, assuming prompt")
    }

    // 권한 상태에 따른 처리
    switch (permissionState) {
      case "denied":
        dispatch(setPermitted(false))
        console.error(
          "위치 정보 접근이 차단되었습니다. 브라우저 설정에서 권한을 허용해주세요.",
        )
        // 사용자에게 설정 변경 안내 메시지 표시 로직 추가 (알림 등)
        return null

      case "granted":
        // 이미 권한이 있으므로 위치 정보 바로 요청
        dispatch(setPermitted(true))
        break

      case "prompt":
      default:
        // 권한 상태가 불확실하거나 물어봐야 하는 상태
        // Safari에서는 여기로 오게 됨
        console.log("Permission needs to be requested")
        dispatch(setPermitted(false)) // 아직 권한 없음
        break
    }

    // 위치 정보 요청 시도 (권한 요청 포함)
    // Safari에서는 이 호출을 통해 권한 프롬프트가 표시됨
    console.log("Requesting position...")

    // 사용자가 더 오래 응답할 수 있도록 타임아웃 증가
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          position => {
            // 성공 시 권한이 부여된 것으로 간주
            dispatch(setPermitted(true))
            resolve(position)
          },
          error => {
            console.error("Geolocation error:", error.code, error.message)

            // 권한 거부 시 처리
            if (error.code === 1) {
              // PERMISSION_DENIED
              dispatch(setPermitted(false))
            }

            reject(new Error(error.message))
          },
          {
            enableHighAccuracy: true,
            timeout: 10000, // 10초로 증가 (Safari에서 더 널널하게)
            maximumAge: 0,
          },
        )
      },
    )

    // 위치 정보 성공적으로 가져왔으면 반환
    console.log("Position successfully fetched:", position.coords)
    return position
  } catch (error) {
    console.error("위치 정보를 가져오는 중 오류 발생:", error)

    // 에러 종류에 따른 처리
    if (error instanceof GeolocationPositionError) {
      // GeolocationPositionError 타입 처리
      switch (error.code) {
        case 1: // PERMISSION_DENIED
          dispatch(setPermitted(false))
          // Safari에서는 권한 거부 시 사용자에게 안내
          showPermissionInstructions()
          break
        case 2: // POSITION_UNAVAILABLE
          console.error("위치 정보를 사용할 수 없습니다.")
          break
        case 3: // TIMEOUT
          console.error("위치 정보 요청 시간이 초과되었습니다.")
          break
      }
    }
    return null
  }
}

// 사용자가 명시적으로 현재 위치 버튼을 눌렀을 때 호출
export const updateGPSByUser = (): AppThunk => async dispatch => {
  const position = await fetchGPSPosition(dispatch)
  if (position) {
    const { latitude, longitude } = position.coords
    dispatch(
      setGPS({
        lat: latitude,
        lng: longitude,
      }),
    )
    dispatch(setGPSFetchedByUser(true))
    dispatch(setHasGPSData(true))
    console.log("GPS updated by user:", { latitude, longitude })
  }
}

// GPS 권한이 있을 때 마커만 표시 (지도 이동 없음)
export const fetchGPSForMarkerOnly = (): AppThunk => async dispatch => {
  try {
    // 권한 확인
    if ("permissions" in navigator) {
      try {
        const permissionResult = await navigator.permissions.query({
          name: "geolocation",
        })
        if (permissionResult.state === "granted") {
          // 권한이 있으면 GPS 조회 (마커만 표시)
          const position = await fetchGPSPosition(dispatch)
          if (position) {
            const { latitude, longitude } = position.coords
            dispatch(
              setGPS({
                lat: latitude,
                lng: longitude,
              }),
            )
            // 지도 중심 이동 없이 마커만 표시
            dispatch(setGPSFetchedByUser(false))
            dispatch(setHasGPSData(true))
            console.log("GPS fetched for marker only:", {
              latitude,
              longitude,
            })
          }
        }
      } catch (error) {
        // Safari에서는 권한 API가 완전히 지원되지 않을 수 있음
        console.log("Permission API not fully supported, skipping auto GPS")
      }
    }
  } catch (error) {
    console.error("GPS 조회 중 오류:", error)
  }
}

// 페이지 로드 시 권한이 있으면 자동으로 GPS 조회
export const initializeGPS = (): AppThunk => async dispatch => {
  try {
    // 권한 확인
    if ("permissions" in navigator) {
      try {
        const permissionResult = await navigator.permissions.query({
          name: "geolocation",
        })
        if (permissionResult.state === "granted") {
          // 권한이 있으면 자동으로 GPS 조회
          const position = await fetchGPSPosition(dispatch)
          if (position) {
            const { latitude, longitude } = position.coords
            dispatch(
              setGPS({
                lat: latitude,
                lng: longitude,
              }),
            )
            // 자동 조회는 지도 중심 이동은 하지 않지만 마커는 표시
            dispatch(setGPSFetchedByUser(false))
            dispatch(setHasGPSData(true))
            console.log("GPS initialized automatically:", {
              latitude,
              longitude,
            })
          }
        }
      } catch (error) {
        // Safari에서는 권한 API가 완전히 지원되지 않을 수 있음
        console.log("Permission API not fully supported, skipping auto GPS")
      }
    }
  } catch (error) {
    console.error("GPS 초기화 중 오류:", error)
  }
}

// Safari에서 권한 설정 방법 안내
function showPermissionInstructions() {
  // 사용자에게 설정 변경 방법 안내
  // 이 함수는 UI 컴포넌트에서 Toast나 Modal로 구현하는 것이 좋습니다
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

  if (isIOS) {
    console.info(
      "iOS 설정 > Safari > 위치 정보에서 권한을 허용해주세요.\n" +
        "또는 설정 > 개인 정보 보호 > 위치 서비스 > Safari에서 허용해주세요.",
    )
    // 여기서 UI 컴포넌트를 통해 사용자에게 알림을 표시
  } else {
    console.info("브라우저 설정에서 위치 정보 권한을 허용해주세요.")
  }
}
